import { Alert, Button, Card, Checkbox, InputNumber, Modal, Space, Table, Typography } from 'antd';
import React, { useState } from 'react';
import { BackfillCompetitionTeamsResultVm, CompetitionTeamsClient } from '../../utils/api';

export default function CompetitionTeamsBackfill(): JSX.Element {
    const [competitionId, setCompetitionId] = useState<number | undefined>();
    const [includeManual, setIncludeManual] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<BackfillCompetitionTeamsResultVm>();
    const [error, setError] = useState<string>();

    const run = async (dryRun: boolean): Promise<void> => {
        setBusy(true);
        setError(undefined);
        setResult(undefined);
        try {
            setResult(
                await new CompetitionTeamsClient().backfillFromImports({
                    dryRun,
                    competitionId,
                    scope: includeManual ? 'all' : 'imports',
                })
            );
        } catch {
            setError('Could not backfill competition teams. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    const confirmApply = (): void => {
        Modal.confirm({
            title: 'Backfill competition teams?',
            content: `Add missing teams from ${
                includeManual ? 'all matches (including manual matches)' : 'imported matches'
            } for ${
                competitionId ? `Arena competition ${competitionId}` : 'all Arena competitions'
            }? Existing and soft-deleted memberships will be preserved.`,
            okText: 'Apply',
            onOk: () => run(false),
        });
    };

    return (
        <Card title="Backfill competition teams" size="small" style={{ marginBottom: 16 }}>
            <Typography.Paragraph>
                Repair competition team lists from imported matches, or include manual matches
                below. Run a dry run first and review the results before applying. Leave the
                competition ID empty to check all competitions.
            </Typography.Paragraph>
            <Space wrap style={{ marginBottom: 12 }}>
                <label htmlFor="backfill-competition-id">Arena competition ID (optional)</label>
                <InputNumber
                    id="backfill-competition-id"
                    min={1}
                    max={2147483647}
                    precision={0}
                    value={competitionId}
                    disabled={busy}
                    onChange={(value) => {
                        setCompetitionId(typeof value === 'number' ? value : undefined);
                        setResult(undefined);
                        setError(undefined);
                    }}
                />
                <Checkbox
                    checked={includeManual}
                    disabled={busy}
                    onChange={(event) => {
                        setIncludeManual(event.target.checked);
                        setResult(undefined);
                        setError(undefined);
                    }}
                >
                    Include manual matches (all matches)
                </Checkbox>
                <Button disabled={busy} onClick={() => void run(true)}>
                    Dry run
                </Button>
                <Button type="primary" disabled={busy} onClick={confirmApply}>
                    Apply
                </Button>
                {busy && <Typography.Text role="status">Backfill running...</Typography.Text>}
            </Space>
            {error && <Alert type="error" showIcon message={error} />}
            {result && (
                <>
                    <Alert
                        type="info"
                        showIcon
                        message={`${result.dryRun ? 'Dry run' : 'Applied'}: ${
                            result.added
                        } added, ${result.wouldAdd} would add, ${
                            result.skippedSoftDeleted
                        } skipped soft-deleted`}
                        style={{ marginBottom: 12 }}
                    />
                    <Table
                        size="small"
                        rowKey={(row) => `${row.competitionId}:${row.teamId}`}
                        dataSource={result.items}
                        pagination={{ pageSize: 10 }}
                        columns={[
                            { title: 'Competition ID', dataIndex: 'competitionId' },
                            { title: 'Team ID', dataIndex: 'teamId' },
                            { title: 'Action', dataIndex: 'action' },
                        ]}
                    />
                </>
            )}
        </Card>
    );
}
